import React, { useState } from 'react';
import { 
  X, 
  FlaskConical, 
  Target, 
  Calendar, 
  Users, 
  BarChart3, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Brain,
  Zap,
  Plus
} from 'lucide-react';

interface CreateExperimentModalProps {
  insight: any;
  isOpen: boolean;
  onClose: () => void;
  onCreateExperiment: (experimentData: any) => void;
}

const CreateExperimentModal: React.FC<CreateExperimentModalProps> = ({ 
  insight, 
  isOpen, 
  onClose, 
  onCreateExperiment 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    hypothesis: '',
    objective: '',
    successMetrics: [''],
    targetAudience: '',
    duration: 30,
    sampleSize: 1000,
    confidenceLevel: 95,
    expectedImpact: 'medium',
    priority: 'medium',
    resources: [''],
    risks: [''],
    tags: [''],
    linkedInsight: insight?.id || '',
    methodology: 'A/B Test',
    startDate: new Date().toISOString().split('T')[0],
    owner: '',
    stakeholders: ['']
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  React.useEffect(() => {
    if (insight && isOpen) {
      // Pre-populate form with insight data
      setFormData(prev => ({
        ...prev,
        title: `${insight.title} - Validation Experiment`,
        hypothesis: generateHypothesis(insight),
        objective: `Validate the market opportunity identified in: ${insight.title}`,
        tags: [insight.category, insight.type, 'market-validation'],
        expectedImpact: insight.impact,
        linkedInsight: insight.id,
        resources: [`Market research from ${insight.title}`, 'Development team', 'Analytics team'],
        risks: insight.risks || ['Market conditions change', 'Technical implementation challenges']
      }));
    }
  }, [insight, isOpen]);

  const generateHypothesis = (insight: any) => {
    const templates = {
      opportunity: `If we implement the opportunity identified in "${insight.title}", then we expect to see ${insight.roi} improvement in key metrics within ${insight.timeframe}.`,
      growth: `By leveraging the growth signal "${insight.title}", we hypothesize that we can achieve ${insight.roi} growth in target segments within ${insight.timeframe}.`,
      threat: `By addressing the threat "${insight.title}", we can mitigate risks and maintain current performance levels while building defensive capabilities.`
    };
    return templates[insight?.type as keyof typeof templates] || `Testing the market insight: ${insight?.title}`;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field: string, index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev].map((item: string, i: number) => 
        i === index ? value : item
      )
    }));
  };

  const addArrayItem = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field as keyof typeof prev], '']
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSubmit = () => {
    // Generate experiment ID
    const experimentId = `EXP-${Date.now().toString().slice(-6)}`;
    
    // Calculate end date
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + formData.duration);

    const experimentData = {
      id: experimentId,
      ...formData,
      status: 'proposed',
      confidence: formData.confidenceLevel,
      impact: formData.expectedImpact,
      signals: formData.tags,
      startDate: formData.startDate,
      expectedEnd: endDate.toISOString().split('T')[0],
      createdFrom: 'market-insight',
      sourceInsight: insight,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };

    onCreateExperiment(experimentData);
    onClose();
  };

  const getStepIcon = (step: number) => {
    if (step < currentStep) return <CheckCircle className="w-5 h-5 text-green-400" />;
    if (step === currentStep) return <Clock className="w-5 h-5 text-indigo-400" />;
    return <div className="w-5 h-5 rounded-full border-2 border-gray-600"></div>;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Experiment Overview</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Experiment Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                placeholder="Enter experiment title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Hypothesis</label>
              <textarea
                value={formData.hypothesis}
                onChange={(e) => handleInputChange('hypothesis', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                placeholder="If we... then we expect... because..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Objective</label>
              <textarea
                value={formData.objective}
                onChange={(e) => handleInputChange('objective', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                placeholder="What are we trying to achieve?"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expected Impact</label>
                <select
                  value={formData.expectedImpact}
                  onChange={(e) => handleInputChange('expectedImpact', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="low">Low Impact</option>
                  <option value="medium">Medium Impact</option>
                  <option value="high">High Impact</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                  <option value="critical">Critical Priority</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Experiment Design</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Methodology</label>
                <select
                  value={formData.methodology}
                  onChange={(e) => handleInputChange('methodology', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="A/B Test">A/B Test</option>
                  <option value="Multivariate Test">Multivariate Test</option>
                  <option value="User Interview">User Interview</option>
                  <option value="Survey">Survey</option>
                  <option value="Prototype Test">Prototype Test</option>
                  <option value="Market Research">Market Research</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Target Audience</label>
                <input
                  type="text"
                  value={formData.targetAudience}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g., Enterprise customers, Mobile users"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Duration (days)</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="365"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sample Size</label>
                <input
                  type="number"
                  value={formData.sampleSize}
                  onChange={(e) => handleInputChange('sampleSize', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confidence Level (%)</label>
                <input
                  type="number"
                  value={formData.confidenceLevel}
                  onChange={(e) => handleInputChange('confidenceLevel', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="80"
                  max="99"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Success Metrics</label>
              {formData.successMetrics.map((metric, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={metric}
                    onChange={(e) => handleArrayInputChange('successMetrics', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., Conversion rate increase by 15%"
                  />
                  {formData.successMetrics.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('successMetrics', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('successMetrics')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Metric</span>
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Resources & Timeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Experiment Owner</label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={(e) => handleInputChange('owner', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g., Sarah Chen"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Required Resources</label>
              {formData.resources.map((resource, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={resource}
                    onChange={(e) => handleArrayInputChange('resources', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., Development team, Analytics tools"
                  />
                  {formData.resources.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('resources', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('resources')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Resource</span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Stakeholders</label>
              {formData.stakeholders.map((stakeholder, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={stakeholder}
                    onChange={(e) => handleArrayInputChange('stakeholders', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., Product Manager, Marketing Team"
                  />
                  {formData.stakeholders.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('stakeholders', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('stakeholders')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Stakeholder</span>
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Risk Assessment & Tags</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Potential Risks</label>
              {formData.risks.map((risk, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={risk}
                    onChange={(e) => handleArrayInputChange('risks', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., Technical implementation challenges"
                  />
                  {formData.risks.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('risks', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('risks')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Risk</span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
              {formData.tags.map((tag, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => handleArrayInputChange('tags', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., mobile, personalization, AI"
                  />
                  {formData.tags.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('tags', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('tags')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Tag</span>
              </button>
            </div>

            {/* Summary */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Experiment Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Title:</span>
                  <span className="text-white ml-2">{formData.title}</span>
                </div>
                <div>
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white ml-2">{formData.duration} days</span>
                </div>
                <div>
                  <span className="text-gray-400">Sample Size:</span>
                  <span className="text-white ml-2">{formData.sampleSize.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Confidence:</span>
                  <span className="text-white ml-2">{formData.confidenceLevel}%</span>
                </div>
                <div>
                  <span className="text-gray-400">Impact:</span>
                  <span className="text-white ml-2 capitalize">{formData.expectedImpact}</span>
                </div>
                <div>
                  <span className="text-gray-400">Priority:</span>
                  <span className="text-white ml-2 capitalize">{formData.priority}</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-900 rounded-t-lg">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-indigo-900 text-indigo-300 border border-indigo-600">
              <FlaskConical className="w-5 h-5" />
              <span className="font-medium">Create Experiment</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">From Market Insight</h2>
              <p className="text-gray-400 text-sm">{insight?.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colours p-2 rounded-lg hover:bg-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className="flex items-center space-x-2">
                  {getStepIcon(step)}
                  <span className={`text-sm font-medium ${
                    step <= currentStep ? 'text-white' : 'text-gray-500'
                  }`}>
                    Step {step}
                  </span>
                </div>
                {step < totalSteps && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    step < currentStep ? 'bg-green-400' : 'bg-gray-600'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-gray-400">
            {currentStep === 1 && 'Define experiment overview and objectives'}
            {currentStep === 2 && 'Configure experiment design and methodology'}
            {currentStep === 3 && 'Set resources, timeline, and ownership'}
            {currentStep === 4 && 'Review risks and finalize experiment details'}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700 bg-gray-900 rounded-b-lg">
          <div className="text-sm text-gray-400">
            Step {currentStep} of {totalSteps}
          </div>
          <div className="flex items-center space-x-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colours"
              >
                Previous
              </button>
            )}
            {currentStep < totalSteps ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colours"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colours flex items-center space-x-2"
              >
                <FlaskConical className="w-4 h-4" />
                <span>Create Experiment</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateExperimentModal;